import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { Container, SideDrawerContainer } from '../../components'
import {
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    MenuItem,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import { CreateAndEditRFP } from './CreateAndEditRFP'
import { RFP, RFPStatus, RFPType } from '../../models'
import { axiosInstance, getDifferenceInDays } from '../../helpers'
import { useStyles } from '../../styles'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { useBlanket, usePagination, useService, useUser } from '../../hooks'
import { Tune, BarChart } from '@material-ui/icons'
import SearchIcon from '@material-ui/icons/Search'
import { RFPFilterDrawer } from './RFPFilterDrawer'
import { Pagination } from '@material-ui/lab'
import { RFPFilterChips } from './RFPFilterChips'
import { rfpApi } from '../../contexts'
import { MarketAnalysisTool } from '../MarketAnalysisTool/MarketAnalysisTool'
import { ApartmentRFPDetail } from './ApartmentRFPDetail'
import { RFPCard } from './RFPCard'
import { RequestRFPModal } from '../Workforce/RequestRFPModal'
import { Routes } from '../../navigation/config/routes'

const PUBLISHED_RFP_LIST = 0
const RFP_DRAFTS = 1
const CLOSED_RFPS = 2

interface Props {
    folio?: boolean
}

export const RFPDashboard = (props: Props) => {
    const { folio } = props
    const rfpState = rfpApi()

    const { workspaceUser, rootUser, organization } = useUser()
    const [openRequestRFP, setOpenRequestRFP] = useState(false)

    const [createOrEditRFP, setCreateOrEditRFP] = useState(false)
    const [selectedTab, setSelectedTab] = useState<number>(PUBLISHED_RFP_LIST)
    const [openFilterDrawer, setOpenFilterDrawer] = useState(false)
    const [viewMode, setViewMode] = useState(false)

    const [openRFPDetail, setOpenRFPDetail] = useState(false)
    const [initialBidId, setInitialBidId] = useState<number | null>(null)
    const history = useHistory()
    const location = useLocation()
    const { rfpId } = useParams<{ rfpId?: string }>()
    const openedFromUrl = useRef(false)

    const contentRef = useRef<HTMLDivElement>(null)
    const [openMarketAnalysis, setOpenMarketAnalysis] = useState(false)
    const analyticRect = useBlanket(openMarketAnalysis, contentRef)

    const serviceList = useSelector(
        (state: RootState) => state.service.serviceList,
    )

    const theme = useTheme()
    const classes = useStyles()

    const { getServiceList } = useService()

    const apartmentList = useMemo(
        () =>
            rootUser?.workspaces
                .map((workspace) => workspace.active_workspace)
                .filter(
                    (workspace) =>
                        workspace.company_type !== 'VENDOR' &&
                        Number(workspace.organization) === Number(organization),
                )
                .sort((a, b) => a.name.localeCompare(b.name)) ?? [],
        [rootUser, organization],
    )

    const selectedApartmentId = rfpState.rfpFilterState.apartmentId
    const selectedApartment = apartmentList.find(
        (apartment) => apartment.id === selectedApartmentId,
    )

    const listPath = (apartmentId?: number | null) => {
        if (folio) {
            return apartmentId
                ? `${Routes.organization.rfp.rfpDashboard}?apartment=${apartmentId}`
                : Routes.organization.rfp.rfpDashboard
        }
        return Routes.apartment.rfp.rfpDashboard
    }

    const detailPath = (rfpId: number, bidId?: number | null) => {
        if (folio) {
            const params = new URLSearchParams()
            if (bidId) {
                params.set('bid', String(bidId))
            }
            if (selectedApartmentId) {
                params.set('apartment', String(selectedApartmentId))
            }
            const query = params.toString()
            return query
                ? `/organization-rfp/${rfpId}?${query}`
                : `/organization-rfp/${rfpId}`
        }
        return bidId
            ? `/rfp-dashboard/${rfpId}?bid=${bidId}`
            : `/rfp-dashboard/${rfpId}`
    }

    const onMount = async () => {
        await rfpState.getRFPList({
            params: {
                type: `${RFPType.INVITE_ONLY},${RFPType.PUBLIC}`,
                ...(folio ? { organization_scope: true } : {}),
            },
        })
        await getServiceList({})
    }

    const cleanUp = () => {
        rfpState.clearState()
    }

    useEffect(() => {
        onMount()
        return cleanUp
    }, [])

    const openDetail = (rfp: RFP, bidId?: number | null) => {
        rfpState.setSelectedRFP(rfp)
        rfpState.getRFPDetail(rfp)
        setOpenRFPDetail(true)
        setInitialBidId(bidId || null)
        const next = detailPath(rfp.id, bidId)
        if (location.pathname + location.search !== next) {
            history.replace(next)
        }
    }

    useEffect(() => {
        if (!rfpId || openedFromUrl.current) {
            return
        }
        const bidParam = Number(
            new URLSearchParams(location.search).get('bid') || '',
        )
        const bidId =
            Number.isFinite(bidParam) && bidParam > 0 ? bidParam : null
        const found = rfpState.rfpList?.find(
            (rfp) => String(rfp.id) === String(rfpId),
        )
        if (found) {
            openedFromUrl.current = true
            openDetail(found, bidId)
            return
        }
        if (!rfpState.rfpList) {
            return
        }
        axiosInstance
            .get<RFP>(`service-contract/rfp/${rfpId}/`)
            .then((res) => {
                openedFromUrl.current = true
                rfpState.setSelectedRFP(res.data)
                setOpenRFPDetail(true)
                setInitialBidId(bidId)
            })
            .catch(() => {
                openedFromUrl.current = true
            })
    }, [rfpId, rfpState.rfpList])

    useEffect(() => {
        if (!folio) {
            return
        }
        const apartmentParam = Number(
            new URLSearchParams(location.search).get('apartment') || '',
        )
        const nextId =
            Number.isFinite(apartmentParam) && apartmentParam > 0
                ? apartmentParam
                : null
        rfpState.setRFPFilterState((prev) =>
            prev.apartmentId === nextId
                ? prev
                : { ...prev, apartmentId: nextId },
        )
    }, [folio, location.search])

    useEffect(() => {
        const statusFilter =
            selectedTab === PUBLISHED_RFP_LIST
                ? RFPStatus.PUBLISHED
                : selectedTab === RFP_DRAFTS
                ? RFPStatus.DRAFT
                : RFPStatus.CLOSED
        rfpState.setRFPFilterState((prev) => ({
            ...prev,
            statusFilter,
        }))
    }, [selectedTab])

    const sortRFPs = (rfpA: RFP, rfpB: RFP) => {
        let rfpATimeLeft = 0
        let rfpBTimeLeft = 0
        const today = new Date()

        if (rfpA.project_submission_deadline) {
            rfpATimeLeft = getDifferenceInDays(
                new Date(rfpA.project_submission_deadline),
                today,
            )
        }
        if (rfpB.project_submission_deadline) {
            rfpBTimeLeft = getDifferenceInDays(
                new Date(rfpB.project_submission_deadline),
                today,
            )
        }

        if (rfpATimeLeft > rfpBTimeLeft) {
            return 1
        }
        return -1
    }

    const tabStyle: React.CSSProperties = {
        fontWeight: 800,
        fontSize: '15px',
        textTransform: 'none',
    }

    const { page, setPage, pageData, numPages, start, end } = usePagination(
        rfpState.filteredRFPList.sort(sortRFPs) ?? [],
    )

    if (
        !folio &&
        !workspaceUser?.active_workspace.has_infrastructure &&
        workspaceUser?.active_workspace.stripe_subscription?.plan !== 'EZVENDOR'
    ) {
        return <Container />
    }

    if (createOrEditRFP || viewMode) {
        return (
            <CreateAndEditRFP
                onExit={() => {
                    rfpState.setSelectedRFP(null)
                    setCreateOrEditRFP(false)
                    setViewMode(false)
                }}
                serviceList={serviceList}
                viewMode={viewMode}
            />
        )
    }

    if (rfpState.selectedRFP && openRFPDetail) {
        return (
            <ApartmentRFPDetail
                selectedRFP={rfpState.selectedRFP}
                setOpenRFPDetail={(open) => {
                    setOpenRFPDetail(open)
                    if (!open) {
                        setInitialBidId(null)
                        history.replace(listPath(selectedApartmentId))
                    }
                }}
                initialBidId={initialBidId}
                onBidDrawerChange={(bidId) => {
                    if (!rfpState.selectedRFP) {
                        return
                    }
                    history.replace(detailPath(rfpState.selectedRFP.id, bidId))
                }}
            />
        )
    }

    return (
        <SideDrawerContainer open={openFilterDrawer}>
            <Container
                flex={1}
                direction="column"
                style={{ height: 'calc(100vh - 104px)' }}
                ref={contentRef}
            >
                <Container
                    style={{
                        fontWeight: 600,
                        fontSize: '32px',
                    }}
                >
                    Managing Your RFPs
                </Container>
                <Container
                    alignItems="center"
                    style={{ margin: theme.spacing(2, 0) }}
                >
                    {folio && (
                        <TextField
                            select
                            variant="outlined"
                            size="small"
                            label="Property"
                            value={selectedApartmentId ?? ''}
                            onChange={(e) => {
                                const nextId = e.target.value
                                    ? Number(e.target.value)
                                    : null
                                rfpState.setRFPFilterState({
                                    ...rfpState.rfpFilterState,
                                    apartmentId: nextId,
                                })
                                const next = listPath(nextId)
                                if (
                                    location.pathname + location.search !==
                                    next
                                ) {
                                    history.replace(next)
                                }
                            }}
                            style={{
                                minWidth: 220,
                                marginRight: theme.spacing(1),
                            }}
                        >
                            <MenuItem value="">All properties</MenuItem>
                            {apartmentList.map((apartment) => (
                                <MenuItem
                                    key={apartment.id}
                                    value={apartment.id}
                                >
                                    {apartment.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                    <TextField
                        variant="outlined"
                        placeholder="Search"
                        value={rfpState.rfpFilterState.searchText}
                        onChange={(e) => {
                            rfpState.setRFPFilterState({
                                ...rfpState.rfpFilterState,
                                searchText: e.target.value,
                            })
                        }}
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Container flex={1} />
                    <Tooltip
                        title="Market Rate Analysis"
                        style={{ marginRight: theme.spacing(2) }}
                    >
                        <IconButton
                            onClick={() =>
                                setOpenMarketAnalysis(!openMarketAnalysis)
                            }
                            color={openMarketAnalysis ? 'primary' : 'default'}
                            size="small"
                        >
                            <BarChart fontSize="large" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Filter">
                        <IconButton onClick={() => setOpenFilterDrawer(true)}>
                            <Tune fontSize="large" color={'primary'} />
                        </IconButton>
                    </Tooltip>
                    {folio ? (
                        <Tooltip
                            title={
                                selectedApartment
                                    ? ''
                                    : 'Select a property to request RFPs'
                            }
                        >
                            <span>
                                <Button
                                    variant="contained"
                                    disabled={!selectedApartment}
                                    style={{
                                        backgroundColor: selectedApartment
                                            ? '#008C85'
                                            : undefined,
                                        color: selectedApartment
                                            ? 'white'
                                            : undefined,
                                        textTransform: 'none',
                                        marginLeft: theme.spacing(1),
                                    }}
                                    onClick={() => setOpenRequestRFP(true)}
                                >
                                    Request RFPs
                                </Button>
                            </span>
                        </Tooltip>
                    ) : (
                        <Button
                            variant="contained"
                            style={{
                                backgroundColor: '#008C85',
                                color: 'white',
                                textTransform: 'none',
                                cursor: 'pointer',
                                marginLeft: theme.spacing(1),
                            }}
                            onClick={() => {
                                rfpState.setSelectedRFP(null)
                                setCreateOrEditRFP(true)
                            }}
                        >
                            Create RFP
                        </Button>
                    )}
                </Container>
                <RFPFilterChips
                    serviceList={serviceList}
                    rfpFilterState={rfpState.rfpFilterState}
                    setRFPFilterState={rfpState.setRFPFilterState}
                />
                <Container>
                    <Tabs
                        style={{
                            color: 'black',
                            borderBottom: `1px solid ${theme.palette.lightGrey.main}`,
                            flex: 1,
                            textTransform: 'none',
                        }}
                        value={selectedTab}
                        onChange={(_, v) => {
                            if (v !== selectedTab) {
                                setSelectedTab(v)
                                setPage(1)
                            }
                        }}
                        classes={{
                            indicator: classes.indicator,
                        }}
                    >
                        <Tab
                            label="Your Published RFPs"
                            style={{ ...tabStyle }}
                        />
                        <Tab label={`Your Drafts`} style={{ ...tabStyle }} />
                        <Tab label={`Closed RFPs`} style={{ ...tabStyle }} />
                    </Tabs>
                </Container>
                <Container direction="column">
                    {rfpState.loadingState.getRFPList ? (
                        <Container
                            alignItems="center"
                            justifyContent="center"
                            flex={1}
                            style={{ minHeight: 'calc(100vh - 370px)' }}
                        >
                            <CircularProgress size={100} />
                        </Container>
                    ) : (
                        <Container
                            flexWrap="wrap"
                            style={{
                                overflowY: 'auto',
                                maxHeight: 'calc(100vh - 370px)',
                                minHeight: 'calc(100vh - 370px)',
                            }}
                        >
                            {pageData.map((rfp) => {
                                return (
                                    <RFPCard
                                        rfp={rfp}
                                        theme={theme}
                                        managerView={folio}
                                        key={`RFP_CARD_${rfp.id}`}
                                        onClick={() => {
                                            if (
                                                rfp.status === RFPStatus.DRAFT
                                            ) {
                                                setViewMode(false)
                                                setCreateOrEditRFP(true)
                                            } else {
                                                openDetail(rfp)
                                            }
                                            rfpState.setSelectedRFP(rfp)
                                        }}
                                    />
                                )
                            })}
                        </Container>
                    )}
                    <Container
                        style={{
                            borderTop: `1px solid ${theme.palette.grey[400]}`,
                            padding: theme.spacing(1),
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <Pagination
                            count={numPages}
                            page={page}
                            onChange={(_: unknown, newPage: number) => {
                                setPage(newPage)
                            }}
                        />
                        <span>
                            {start} - {end} of{' '}
                            {rfpState.filteredRFPList.length ?? 0}
                        </span>
                    </Container>
                </Container>
                <RFPFilterDrawer
                    open={openFilterDrawer}
                    serviceList={serviceList}
                    onClose={() => setOpenFilterDrawer(false)}
                />
                <MarketAnalysisTool
                    open={openMarketAnalysis}
                    onClose={() => setOpenMarketAnalysis(false)}
                    rect={analyticRect}
                    serviceList={serviceList}
                />
                {folio && selectedApartment && (
                    <RequestRFPModal
                        open={openRequestRFP}
                        apartmentId={selectedApartment.id}
                        apartmentName={selectedApartment.name}
                        dashboardPath={listPath(selectedApartment.id)}
                        onClose={() => setOpenRequestRFP(false)}
                    />
                )}
            </Container>
        </SideDrawerContainer>
    )
}
